<template>
    <hts-drawer
        v-model="visible"
        color="main"
        size="large"
        :title="$t('labels.view')"
        @close="close">
        <hts-select-option
            v-model="formData.type"
            :items="types"
            :label="$t('labels.type')"
            readonly />
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <template v-if="['pobo', 'cobo'].includes(formData.type)">
                <hts-select-cooperator
                    v-model="formData.payToId"
                    :label="$t('labels.payTo')"
                    readonly />
                <hts-select-cooperator-invoicing-information
                    v-model="formData.payToInvoicingInformationId"
                    v-model:name="formData.payToInvoicingInformationName"
                    :disabled="!formData.payToId"
                    :cooperator-id="formData.payToId"
                    :label="$t('labels.selectInvoicingInformation')"
                    :creatable="true"
                    readonly
                    prop="payTo"
                    name-select="payTo" />
                <hts-select-cooperator
                    v-model="formData.collectFromId"
                    :label="$t('labels.collectFrom')"
                    readonly />
                <hts-select-cooperator-invoicing-information
                    v-model="formData.collectFromInvoicingInformationId"
                    v-model:name="formData.collectFromInvoicingInformationName"
                    :disabled="!formData.collectFromId"
                    :cooperator-id="formData.collectFromId"
                    :label="$t('labels.selectInvoicingInformation')"
                    :creatable="true"
                    prop="collectFrom"
                    name-select="collectFrom"
                    readonly />
                <hts-form-input
                    v-model="formData.invoiceNo"
                    :label="$t('labels.invoiceNo')"
                    required />
            </template>
            <template v-else>
                <template v-if="['revenue'].includes(formData.type)">
                    <hts-form-input
                        v-model="formData.customerName"
                        :label="$t('labels.customer')"
                        readonly />
                </template>
                <template v-if="['cost'].includes(formData.type)">
                    <hts-select-cooperator
                        v-model="formData.providerId"
                        v-model:name="formData.providerName"
                        :label="$t('labels.provider')"
                        readonly />
                </template>
                <hts-select-cooperator-invoicing-information
                    v-model="formData.selectInvoicingInformation"
                    v-model:name="formData.selectInvoicingInformationName"
                    :disabled="!formData.customerId"
                    :cooperator-id="formData.customerId"
                    :label="$t('labels.selectInvoicingInformation')"
                    :creatable="true"
                    prop="contact"
                    name-select="contact"
                    readonly />
            </template>

            <hts-form-date
                v-model="formData.invoicedDate"
                :label="$t('labels.invoicedDate')"
                readonly />

            <hts-form-date
                v-model="formData.dueDate"
                :label="$t('labels.dueDate')"
                readonly />

            <hts-form-select
                v-model="formData.currency"
                :items="currencyOptions"
                :label="$t('labels.currency')"
                readonly />

            <hts-form-select
                v-model="formData.paymentMethod"
                :items="paymentMethodOptions"
                :label="$t('labels.paymentMethod')"
                readonly />

            <hts-select-bank-account
                v-if="formData.paymentMethod === 'bank'"
                v-model="formData.bankAccountId"
                v-model:name="formData.bankAccountInfo"
                creatable
                readonly />
        </div>

        <charge-table
            readonly
            :model-value="formData.prices"
            :total-amount="totalAmount"
            :total-tax="totalTax"
            :grand-total="grandTotal" />
    </hts-drawer>
</template>
<script setup lang="ts">
import ChargeTable from './-components/change-table.vue'
const visible = ref<boolean>(false)
const emits = defineEmits(['refreshTables'])
const types = ref([
  { label: $t('labels.revenue'), value: 'revenue' },
  { label: $t('labels.cost'), value: 'cost' },
  { label: $t('labels.pobo'), value: 'pobo' },
  { label: $t('labels.cobo'), value: 'cobo' }
  // { label: 'Tạm ứng', value: 'advance' },
  // { label: 'Hoàn ứng', value: 'repayment' }
])
const formData = ref({
  type: 'cost',
  recipientFrom: '',
  providerId: '',
  providerName: '',
  selectInvoicingInformation: '',
  selectInvoicingInformationName: '',
  payToId: null,
  payToInvoicingInformationId: null,
  payToInvoicingInformationName: '',
  collectFromId: null,
  collectFromInvoicingInformationId: null,
  collectFromInvoicingInformationName: '',
  invoiceNo: '',
  invoicedDate: '',
  dueDate: '',
  currency: 'VND',
  paymentMethod: '',
  prices: [] as { [key: string]: any }[],
  bankAccountId: '',
  bankAccountInfo: '',
  totalAmount: 0,
  totalTax: 0,
  grandTotal: 0
})

const resetForm = () => {
  formData.value = {
    type: 'cost',
    recipientFrom: 'CUSTOMER',
    providerId: '',
    providerName: '',
    selectInvoicingInformation: '',
    selectInvoicingInformationName: '',
    payToId: null,
    payToInvoicingInformationId: null,
    payToInvoicingInformationName: '',
    collectFromId: null,
    collectFromInvoicingInformationId: null,
    collectFromInvoicingInformationName: '',
    invoiceNo: '',
    invoicedDate: '',
    dueDate: '',
    currency: 'VND',
    paymentMethod: '',
    prices: [] as { [key: string]: any }[],
    bankAccountId: '',
    bankAccountInfo: '',
    totalAmount: 0,
    totalTax: 0,
    grandTotal: 0
  }
}

const tripId = ref('')

const open = (tripId: string, debitId: string) => {
  visible.value = true
  getData(tripId, debitId)
}
const getData = async (tripId: string, debitId: string) => {
  const api = {
    url: `/trip/${tripId}/billing-and-costing/payment-note/${debitId}`,
    method: 'get'
  }
  const data: any = await $api(api)
  const resData: any = data.data.value

  if (resData?.success) {
    formData.value = resData.data
  }
}

const currencyOptions = [{ label: 'VND', value: 'VND' }]

const totalAmount = ref(0)
const totalTax = ref(0)
const grandTotal = ref(0)

const paymentMethodOptions = [
  { label: 'Tiền mặt', value: 'cash' },
  { label: 'Chuyển khoản', value: 'bank' }
]

const close = () => {
  resetForm()
  visible.value = false
}

defineExpose({ open })
</script>
